import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";

export interface ForecastLane {
  label: "committed" | "best_case" | "pipeline";
  totalCents: number;
  dealCount: number;
  color: string;
}

export interface AiPrediction {
  predictedCents: number;
  confidenceLowCents: number;
  confidenceHighCents: number;
  confidenceLevel: "high" | "medium" | "low";
  confidencePercent: number;
  reasoning: string;
}

export interface RepQuota {
  repId: string;
  repName: string;
  quotaCents: number;
  achievedCents: number;
  forecastCents: number;
  pctOfQuota: number;
  isOnTrack: boolean;
  guidance: string;
}

export interface ForecastData {
  period: string;
  lanes: ForecastLane[];
  aiPrediction: AiPrediction;
  repQuotas: RepQuota[];
  totalPipelineCents: number;
  teamQuotaCents: number;
  teamAchievedCents: number;
  projectedTeamCents: number;
  aiSummary: string;
}

export function useDealForecast(period: string) {
  const [data, setData] = useState<ForecastData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(() => {
    setIsLoading(true);
    setError(null);
    apiFetch<{ data: ForecastData }>(`/crm/intelligence/forecast?period=${encodeURIComponent(period)}`)
      .then((res) => setData(res.data))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load"))
      .finally(() => setIsLoading(false));
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}
